import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, Button, IconButton } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Breadcrumbs from "../../common/Breadcrumbs";
import { fetchProductData } from "../../api/productDataAPI";
import arrowLeftIcon from "../../assets/arrow-left.svg";
import arrowRightIcon from "../../assets/arrow-right.svg";
import editIcon from "../../assets/edit-icon.svg";
import "./ProductDetails.css";
import { useDispatch } from "react-redux";
import { addItem } from "../../redux/features/slices/cartSlice";
import { useRole } from "../../context/RoleContext";
import { productEndPoints } from "../../constants/endpoints";
import { mapProduct } from "../../utils/productListMapping";
import Toast from "../../common/Toast";
import {
  getCartByCustomerId,
  updateCartByCustomerIdService,
} from "../../service/cartService";
import { cartDataMapper } from "../../utils/cartDataMapper";
import CustomDropdown from "../../common/CustomDropdown";
import { sendCustomEvents } from "../../ga";

interface Product {
  id: string;
  name: string;
  price: number;
  Description: string;
  isOutOfStock: boolean;
  mainImage: string;
  images: string[];
  flavor: string;
}

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { role } = useRole();

  const [product, setProduct] = useState<any>(null);
  const [currentImage, setCurrentImage] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [count, setCount] = useState(1);
  const [value, setValue] = useState("20");
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const imagesPerPage = 4;
  const orgId = localStorage.getItem("orgId");

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const foundProduct = await fetchProductData(
          id,
          productEndPoints.getProducts
        );
        const mappedData = mapProduct(foundProduct);
        setProduct(mappedData);
        if (mappedData?.mainImage) {
          setCurrentImage(mappedData.mainImage);
          fetchCartDetails(mappedData?.id);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
        setToastMessage("Something Went Wrong");
        setError(true);
        setShowToast(true);
      }
    };

    const fetchCartDetails = async (id: string) => {
      try {
        const cartData = await getCartByCustomerId();
        const filteredCartByProduct = cartDataHandler(cartData, id);
        if (filteredCartByProduct.productId) {
          setValue(filteredCartByProduct.quantity);
          setCount(+filteredCartByProduct.unit);
        }
      } catch (error: any) {
        console.log("error in fetching cart", error.message);
      }
    };

    if (id) fetchProductDetails();
  }, [id]);

  const cartDataHandler = (data: any, productId: string) => {
    const filteredData = data?.Products?.filter(
      (product: any) => product.productId === productId
    );
    return filteredData[0];
  };

  const handleImageClick = (imageUrl: string) => {
    setCurrentImage(imageUrl);
  };
  const slideLeft = () => {
    setCurrentImageIndex((prev) => {
      const newIndex = (prev - 1 + productImages.length) % productImages.length;
      setCurrentImage(productImages[newIndex]);
      return newIndex;
    });
  };

  const slideRight = () => {
    setCurrentImageIndex((prev) => {
      const newIndex = (prev + 1) % productImages.length;
      setCurrentImage(productImages[newIndex]);
      return newIndex;
    });
  };

  const getLinks = () => {
    if (role === "seller" || location.pathname.startsWith("/seller")) {
      return [
        "/seller/create-new-customer",
        "/seller/products",
        `/seller/products/product/${id}`,
      ];
    }
    if (role === "customer" || location.pathname.startsWith("/customer")) {
      return [
        "/customer/home",
        "/customer/products",
        `/customer/products/product/${id}`,
      ];
    }
  };

  const handleDecrement = () => setCount((prev) => Math.max(prev - 1, 1));
  const handleIncrement = () => setCount((prev) => prev + 1);
  const handleEdit = (id: string) =>
    navigate(`/${orgId}/seller/edit-product/${id}`);

  const handleChange = (selectedValue: string) => {
    setValue(selectedValue);
  };

  const handleAddToCart = async () => {
    if (product?.isOutOfStock) {
      setToastMessage(
        "This product is out of stock. We'll notify you when it's available."
      );
      setError(true);
      setShowToast(true);
      return;
    }

    let availableQuantity = 0;
    if (+value === 20) availableQuantity = product.availableQuantity20L || 0;
    else if (+value === 30)
      availableQuantity = product.availableQuantity30L || 0;
    else if (+value === 50)
      availableQuantity = product.availableQuantity50L || 0;
    console.log(availableQuantity, "availabale++");

    if (count <= availableQuantity) {
      try {
        const updateCartData = {
          productId: product.id,
          unit: +count,
          quantity: +value,
        };

        const updatedCart = await updateCartByCustomerIdService({
          cartData: [updateCartData],
        });
        const storeData = cartDataMapper(updatedCart?.Products);
        dispatch(addItem(storeData));
        setToastMessage("Product added to Cart successfully");
        setError(false);
        setShowToast(true);
      } catch (error: any) {
        console.log("error adding to cart", error.message);
        setToastMessage("Something went wrong when updating the cart");
        setError(true);
        setShowToast(true);
      }
    } else {
      setToastMessage(`Product is out of stock for ${value}L`);
      setError(true);
      setShowToast(true);
      return;
    }
  };

  // Derived state
  const isSellerRoute = location.pathname.includes("/seller/products");
  const productImages = product?.images || [];
  const visibleImages = productImages.slice(
    currentImageIndex,
    currentImageIndex + imagesPerPage
  );

  return (
    <>
      <Breadcrumbs
        paths={["Home", "Products", "Product Details"]}
        links={getLinks()}
      />

      <Box className="product-details-container">
        <Box className="product-details-content">
          {/* Image Section */}
          <Box className="product-images-section">
            <Box className="product-main-image">
              <img
                src={currentImage || "image1.jpg"}
                alt="Product"
                className={`main-image ${!isSellerRoute && product?.isOutOfStock ? "out-of-stock" : ""}`}
                data-testid="main-image"
              />
            </Box>

            {/* Thumbnails */}
            <Box className="product-thumbnails">
              <IconButton
                onClick={slideLeft}
                disabled={currentImageIndex === 0}
                className="no-hover-effect"
              >
                <img
                  src={arrowLeftIcon}
                  alt="Previous"
                  className="arrow-button"
                  data-testid="prev-button"
                />
              </IconButton>

              <Box
                className="thumbnail-container"
                data-testid="thumbnail-container"
              >
                {visibleImages.map((image: string, index: number) => {
                  return (
                    <img
                      key={index}
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className={`product-thumbnail ${!isSellerRoute && product?.isOutOfStock ? "out-of-stock" : ""} ${currentImage === image ? "selected" : ""}`}
                      onClick={() => {
                        handleImageClick(image);
                      }}
                      data-testid={`thumbnail-image-${index}`}
                    />
                  );
                })}
              </Box>

              <IconButton
                onClick={slideRight}
                disabled={
                  currentImageIndex >= productImages.length - imagesPerPage
                }
                className="no-hover-effect"
                data-testid="next-button"
              >
                <img src={arrowRightIcon} alt="Next" className="arrow-button" />
              </IconButton>
            </Box>
          </Box>

          {/* Details Section */}
          <Box className="product-details-section">
            {/* Title and Edit */}
            <Box className="product-editSection">
              <Typography
                variant="h2"
                className={`product-Name ${!isSellerRoute && product?.isOutOfStock ? "out-of-stock" : ""}`}
              >
                {product?.name}
              </Typography>
              {isSellerRoute && (
                <IconButton onClick={() => handleEdit(product?.id)}>
                  <img src={editIcon} alt="edit-icon" className="edit-button" />
                </IconButton>
              )}
            </Box>

            {/* Price */}
            <Box className="price-ofs-section">
              <Typography
                variant="h2"
                className={`product-Price ${!isSellerRoute && product?.isOutOfStock ? "out-of-stock" : ""}`}
              >
                ₹ {product?.price.toLocaleString("en-IN")} /L
              </Typography>
              {!isSellerRoute && (
                <Typography variant="h2" className="out-of-stock">
                  {product?.isOutOfStock ? "Out of stock" : ""}
                </Typography>
              )}
            </Box>

            <Divider className="divider" />

            {/* Description */}

            <Typography variant="body1" className="product-description">
              {product?.Description}
            </Typography>

            {/* Actions (Only for non-seller route) */}
            {!isSellerRoute && (
              <Box className="product-actions">
                <Box className="select-quantity">
                  <Box className="litre-dropdown">
                    <CustomDropdown
                      label={`${value} L`}
                      value={value}
                      options={[
                        { value: "20", label: "20 L" },
                        { value: "30", label: "30 L" },
                        { value: "50", label: "50 L" },
                      ]}
                      onChange={handleChange}
                      customStyles={{
                        button: { borderRadius: "60px" },
                        labelClass: "dropdown-label-style",
                        optionClass: "dropdown-option-style",
                      }}
                    />
                  </Box>
                  {/* Quantity Control */}
                  <Box className="quantity-button">
                    <IconButton
                      onClick={handleDecrement}
                      aria-label="decrease"
                      className="quantity-icon"
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography className="quantity-count">{count}</Typography>
                    <IconButton
                      onClick={handleIncrement}
                      aria-label="increase"
                      className="quantity-icon"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Add to Cart */}
                <Button
                  className="add-to-cart-button"
                  data-testid="add-to-cart-button"
                  onClick={() => {
                    sendCustomEvents(
                      "Customer",
                      "Clicked Add to Cart",
                      "Add to Cart in Product Description"
                    );
                    handleAddToCart();
                  }}
                >
                  {product?.isOutOfStock ? "Notify me" : "Add to cart"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
        data-testid={error ? "toast-error" : "toast-success"}
      />
    </>
  );
};
